import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Clock, MapPin, SignIn, SignOut, QrCode, Warning } from "@phosphor-icons/react";
import { Spinner, Pill, fmtDT, err } from "../components/hr/hrUtils";
import { TONE_CLASSES } from "../components/assets/assetUtils";

const fmtMins = (m) => `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;

function getFix() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("This device can't share its location"));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: Math.round(p.coords.accuracy || 0) }),
      (e) => reject(new Error(e.code === 1 ? "Location access was blocked — allow it in your browser settings to clock in" : "Couldn't get a GPS fix — try again outside")),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  });
}

/** /hr/clock — geofenced clock in / out. Opened from a site's QR poster (?s=site&k=key) or by picking a site. */
export default function ClockPage() {
  const [params] = useSearchParams();
  const qrSite = params.get("s") || "";
  const [qrKey, setQrKey] = useState(params.get("k") || "");
  const [qrWarn, setQrWarn] = useState("");
  const [st, setSt] = useState(null);
  const [siteId, setSiteId] = useState(qrSite);
  const [busy, setBusy] = useState(false);
  const [fix, setFix] = useState(null);
  const [fixErr, setFixErr] = useState("");

  const load = useCallback(() => API.get("/hr/clock/status").then((r) => setSt(r.data)).catch(() => setSt({ has_record: false, sites: [], today: [] })), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {  // confirm the poster's code is still current before trusting it
    if (!qrSite) return;
    API.get("/hr/clock/qr-check", { params: { s: qrSite, k: params.get("k") || "" } }).then((r) => {
      if (!r.data.valid) { setQrWarn(r.data.reason); setQrKey(""); if (!r.data.site || r.data.site.active === false) setSiteId(""); }
    }).catch(() => {});
  }, [qrSite, params]);
  useEffect(() => { if (!siteId && st?.sites?.length === 1) setSiteId(st.sites[0].id); }, [st, siteId]);
  useEffect(() => { getFix().then((f) => { setFix(f); setFixErr(""); }).catch((e) => setFixErr(e.message)); }, []);

  const act = async (dir) => {
    setBusy(true);
    try {
      const f = await getFix();
      setFix(f); setFixErr("");
      const body = dir === "in" ? { site_id: siteId, k: qrSite === siteId ? qrKey : "", ...f } : { ...f, k: qrKey };
      const r = await API.post(`/hr/clock/${dir}`, body);
      toast.success(r.data.message);
      load();
    } catch (e) {
      if (e?.response) err(e, "Couldn't clock " + dir); else { setFixErr(e.message); toast.error(e.message); }
    } finally { setBusy(false); }
  };

  if (!st) return <Spinner />;
  const site = st.sites?.find((s) => s.id === siteId);
  return (
    <div className="p-4 sm:p-6 max-w-xl mx-auto space-y-5" data-testid="hr-clock-page">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Time & attendance</p>
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2"><Clock size={26} className="text-primary" weight="fill" /> Clock {st.open ? "out" : "in"}</h1>
        <p className="text-sm text-muted-foreground mt-1">Clock-in only works when you're within the site's radius — your location is checked each time.</p>
      </div>

      {qrWarn && <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm flex gap-2" data-testid="hr-clock-qr-stale"><Warning size={18} className="text-amber-600 shrink-0" /> {qrWarn}</div>}

      {!st.has_record ? (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm flex gap-2" data-testid="hr-clock-no-record"><Warning size={18} className="text-amber-600 shrink-0" /> Your login isn't linked to an HR record yet — ask HR to create one before you can clock in.</div>
      ) : (
        <div className="rounded-2xl border border-border bg-card p-5 space-y-4" data-testid="hr-clock-card">
          {st.open ? (
            <div className="space-y-1">
              <Pill tone={TONE_CLASSES.ok} testid="hr-clock-open-pill">Clocked in</Pill>
              <p className="text-lg font-semibold" data-testid="hr-clock-open-site">{st.open.site_name}</p>
              <p className="text-sm text-muted-foreground">Since {fmtDT(st.open.clock_in.at)} · {fmtMins(st.open.minutes)} so far</p>
            </div>
          ) : (
            <div>
              <label className="text-xs font-medium">Site</label>
              {qrSite && site && qrKey ? <p className="text-lg font-semibold flex items-center gap-2" data-testid="hr-clock-qr-site"><QrCode size={18} /> {site.name}</p> : (
                <select value={siteId} onChange={(e) => setSiteId(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-border bg-background px-2 text-sm" data-testid="hr-clock-site">
                  <option value="">Pick where you are…</option>
                  {st.sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              )}
              {st.sites.length === 0 && <p className="text-xs text-muted-foreground mt-1">No sites set up yet — HR adds them under HR &gt; Attendance.</p>}
            </div>
          )}
          <div className="text-xs text-muted-foreground flex items-center gap-1" data-testid="hr-clock-fix">
            <MapPin size={14} /> {fix ? `Location ready (±${fix.accuracy} m)` : fixErr || "Getting your location…"}
          </div>
          {st.open ? (
            <Button className="w-full h-12 text-base" variant="destructive" disabled={busy} onClick={() => act("out")} data-testid="hr-clock-out-btn"><SignOut size={18} className="mr-2" /> {busy ? "Checking location…" : "Clock out"}</Button>
          ) : (
            <Button className="w-full h-12 text-base" disabled={busy || !siteId} onClick={() => act("in")} data-testid="hr-clock-in-btn"><SignIn size={18} className="mr-2" /> {busy ? "Checking location…" : "Clock in"}</Button>
          )}
        </div>
      )}

      {st.has_record && (
        <div className="rounded-xl border border-border p-4" data-testid="hr-clock-today">
          <div className="flex items-center justify-between"><p className="text-sm font-semibold">Today</p><p className="text-xs text-muted-foreground">This week: {fmtMins(st.week_minutes)}</p></div>
          {st.today.length === 0 ? <p className="text-xs text-muted-foreground mt-1">No clock-ins yet today.</p> : (
            <ul className="mt-2 space-y-1 text-sm">{st.today.map((t) => <li key={t.id} className="flex justify-between"><span>{t.site_name} · {fmtDT(t.clock_in.at).split(" ").slice(-1)}{t.clock_out ? ` → ${fmtDT(t.clock_out.at).split(" ").slice(-1)}` : " → now"}</span><span className="text-muted-foreground">{fmtMins(t.minutes)}</span></li>)}</ul>
          )}
        </div>
      )}
      <p className="text-xs text-muted-foreground"><Link to="/hr/me" className="underline" data-testid="hr-clock-back">Back to My HR</Link></p>
    </div>
  );
}
